import { useState, useRef } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { domicilisDB, configDB } from '../services/db'
import { exportSlots, importSlots, isEncrypted, descarregarBlob } from '../services/exportService'

export const DIFICULTAT_CONFIG = {
  molt_baixa: { label: 'Molt Baixa', emoji: '🟢', color: '#34C759', bg: '#F0FFF4' },
  baixa:      { label: 'Baixa',      emoji: '🟡', color: '#b5a000', bg: '#FFFFF0' },
  normal:     { label: 'Normal',     emoji: '⚪', color: '#636366', bg: '#F2F2F7' },
  alta:       { label: 'Alta',       emoji: '🟠', color: '#FF9500', bg: '#FFF3E0' },
  molt_alta:  { label: 'Molt Alta',  emoji: '🔴', color: '#FF3B30', bg: '#FFF0F0' },
}

export function BadgeDificultat({ dificultat = 'normal', style = {} }) {
  const cfg = DIFICULTAT_CONFIG[dificultat] ?? DIFICULTAT_CONFIG.normal
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '2px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 500,
      ...style,
    }}>
      {cfg.emoji} {cfg.label}
    </span>
  )
}

const FORM_BUIT = {
  codi: 'HADO1', adreça: '', lat: '', lng: '', notes: '',
  persona_nom: '', telefon: '', estat: null,
  dificultat: 'normal', num_visites: 1, interval_visites: 60,
}

const ESTATS = [
  { val: null,     label: 'Cap',        color: '#6b7280' },
  { val: 'ingres', label: '🟢 Ingrés',  color: '#34c759' },
  { val: 'alta',   label: '🔴 Alta',    color: '#ff3b30' },
]

// ── Utilitats ─────────────────────────────────────────────────────────────────

function guiar(dom) {
  const { lat, lng } = dom.dades_json ?? {}
  if (!lat || !lng) return
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
  window.open(
    ios ? `maps://?daddr=${lat},${lng}&dirflg=d`
        : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
    '_blank'
  )
}

function trucar(tel) {
  window.location.href = `tel:${tel.replace(/\s/g, '')}`
}

// ── Components interns ────────────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div style={S.overlayFons} onClick={onClose}>
      <div style={S.overlayContingut} onClick={e => e.stopPropagation()}>
        <button style={S.closeBtn} onClick={onClose}>✕</button>
        {children}
      </div>
    </div>
  )
}

function DetallModal({ dom, onClose, onEdit, onVeureTel }) {
  const lat   = dom.dades_json?.lat
  const lng   = dom.dades_json?.lng
  const estat = dom.dades_json?.estat
  const nom   = dom.dades_json?.persona_nom
  const tel   = dom.dades_json?.telefon
  const notes = dom.dades_json?.notes

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={S.modalTitol}>{dom.codi}</h2>
        <button style={S.btnEditModal} onClick={onEdit}>✎ Editar</button>
      </div>
      {dom.adreça && <p style={S.detallAdreca}>{dom.adreça}</p>}

      {estat === 'ingres' && (
        <div style={{ ...S.estatBadgeGran, backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
          🟢 NOU INGRÉS
        </div>
      )}
      {estat === 'alta' && (
        <div style={{ ...S.estatBadgeGran, backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
          🔴 ALTA
        </div>
      )}

      {nom && (
        <div style={S.detallFila}>
          <span style={S.detallIcon}>👤</span>
          <span style={S.detallText}>{nom}</span>
        </div>
      )}

      {tel && (
        <div style={{ marginBottom: 12 }}>
          <div style={S.detallFila}>
            <span style={S.detallIcon}>📞</span>
            <span style={S.detallText}>{tel}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, paddingLeft: 30 }}>
            <button style={S.btnTrucar} onClick={() => trucar(tel)}>📞 Trucar</button>
            <button style={{ ...S.btnTrucar, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }} onClick={() => onVeureTel(tel)}>
              👁️ Veure
            </button>
          </div>
        </div>
      )}

      {notes && (
        <div style={S.detallFila}>
          <span style={S.detallIcon}>📝</span>
          <span style={{ ...S.detallText, color: '#6b7280', fontStyle: 'italic' }}>{notes}</span>
        </div>
      )}

      {lat && lng && (
        <div style={S.detallFila}>
          <span style={S.detallIcon}>📍</span>
          <span style={{ ...S.detallText, fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>
            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
          </span>
        </div>
      )}

      {lat && lng && (
        <button style={{ ...S.btnPrimariBlock, marginTop: 12 }} onClick={() => guiar(dom)}>
          🧭 Guiar
        </button>
      )}
    </Overlay>
  )
}

function TelModal({ telefon, onClose }) {
  const [copiat, setCopiat] = useState(false)

  function copiar() {
    navigator.clipboard?.writeText(telefon)
      .then(() => { setCopiat(true); setTimeout(() => setCopiat(false), 2500) })
      .catch(() => {})
  }

  return (
    <Overlay onClose={onClose}>
      <h2 style={S.modalTitol}>📞 Telèfon de contacte</h2>
      <p style={S.telNumero}>{telefon}</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          style={{ ...S.btnPrimariBlock, flex: 1, marginTop: 0 }}
          onClick={() => window.location.href = `tel:${telefon.replace(/\s/g, '')}`}
        >
          📞 Trucar
        </button>
        <button
          style={{
            ...S.btnPrimariBlock, flex: 1, marginTop: 0,
            backgroundColor: copiat ? '#34c759' : '#f3f4f6',
            color: copiat ? '#fff' : '#374151',
          }}
          onClick={copiar}
        >
          {copiat ? '✓ Copiat' : '📋 Copiar'}
        </button>
      </div>
    </Overlay>
  )
}

// ── Pàgina principal ──────────────────────────────────────────────────────────

export default function DomicilisPage() {
  const domicilis = useLiveQuery(() => domicilisDB.getAll(), []) ?? []
  const config    = useLiveQuery(() => configDB.getOrDefault(), [])
  const codis     = Array.from({ length: config?.num_slots ?? 40 }, (_, i) => `HADO${i + 1}`)

  const [showForm, setShowForm]           = useState(false)
  const [editId, setEditId]               = useState(null)
  const [form, setForm]                   = useState(FORM_BUIT)
  const [showExport, setShowExport]       = useState(false)
  const [exportPwd, setExportPwd]         = useState('')
  const [showImportPwd, setShowImportPwd] = useState(false)
  const [importPwd, setImportPwd]         = useState('')
  const [pendingFile, setPendingFile]     = useState(null)
  const [error, setError]                 = useState(null)
  const [toast, setToast]                 = useState(null)
  const [geocodant, setGeocodant]               = useState(false)
  const [detallDom, setDetallDom]               = useState(null)
  const [telModal, setTelModal]                 = useState(null)
  const [mostrarConfirmacioAlta, setMostrarConfirmacioAlta] = useState(false)
  const [confirmat1, setConfirmat1]             = useState(false)
  const importRef                               = useRef()

  function mostrarToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  function obrirNou() {
    const usats  = new Set(domicilis.map(d => d.codi))
    const lliure = codis.find(c => !usats.has(c)) ?? 'HADO1'
    setForm({ ...FORM_BUIT, codi: lliure })
    setEditId(null)
    setError(null)
    setShowForm(true)
  }

  function obrirEdicio(dom) {
    setForm({
      codi:            dom.codi,
      adreça:          dom.adreça ?? '',
      lat:             dom.dades_json?.lat ?? '',
      lng:             dom.dades_json?.lng ?? '',
      notes:           dom.dades_json?.notes ?? '',
      persona_nom:     dom.dades_json?.persona_nom ?? '',
      telefon:         dom.dades_json?.telefon ?? '',
      estat:           dom.dades_json?.estat ?? null,
      dificultat:      dom.dades_json?.dificultat      ?? 'normal',
      num_visites:     dom.dades_json?.num_visites     ?? 1,
      interval_visites:dom.dades_json?.interval_visites ?? 60,
    })
    setEditId(dom.id)
    setError(null)
    setShowForm(true)
  }

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })) }

  async function geocodificar() {
    if (!form.adreça.trim()) return setError('Introdueix una adreça primer')
    setGeocodant(true)
    setError(null)
    try {
      const q   = encodeURIComponent(`${form.adreça}, Barcelona`)
      const url = `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`
      const res = await fetch(url, { headers: { 'User-Agent': 'rutes-hado/1.0' } })
      const data = await res.json()
      if (!data.length) throw new Error('Adreça no trobada')
      setF('lat', parseFloat(data[0].lat).toFixed(6))
      setF('lng', parseFloat(data[0].lon).toFixed(6))
      mostrarToast('Coordenades obtingudes')
    } catch (e) {
      setError(e.message)
    } finally {
      setGeocodant(false)
    }
  }

  async function guardar() {
    if (!form.codi) return setError('El codi és obligatori')
    setError(null)
    const dades_json = {
      lat:             form.lat !== '' ? parseFloat(form.lat) : null,
      lng:             form.lng !== '' ? parseFloat(form.lng) : null,
      notes:           form.notes.trim(),
      persona_nom:     form.persona_nom.trim(),
      telefon:         form.telefon.trim(),
      estat:           form.estat,
      dificultat:      form.dificultat ?? 'normal',
      num_visites:     form.num_visites ?? 1,
      interval_visites:form.interval_visites ?? 60,
    }
    try {
      if (editId) {
        await domicilisDB.update(editId, { codi: form.codi, adreça: form.adreça.trim(), dades_json })
        mostrarToast('Domicili actualitzat')
      } else {
        await domicilisDB.create({ codi: form.codi, adreça: form.adreça.trim(), dades_json })
        mostrarToast('Domicili creat')
      }
      setShowForm(false)
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleAltaDomicili() {
    try {
      await domicilisDB.delete(editId)
      await domicilisDB.create({ codi: form.codi, adreça: '', dades_json: {} })
      setMostrarConfirmacioAlta(false)
      setConfirmat1(false)
      setShowForm(false)
      mostrarToast(`${form.codi} donat d'alta`)
    } catch (e) {
      setError(e.message)
    }
  }

  async function eliminar(dom) {
    if (!confirm(`Eliminar ${dom.codi}?`)) return
    await domicilisDB.delete(dom.id)
    if (detallDom?.id === dom.id) setDetallDom(null)
    mostrarToast('Domicili eliminat')
  }

  // ── Exportació ──────────────────────────────────────────────────────────────

  async function confirmarExport() {
    try {
      const blob = exportSlots(domicilis, exportPwd.trim() || null)
      const data = new Date().toISOString().slice(0, 10)
      descarregarBlob(blob, `hado-domicilis-${data}.hado`)
      setShowExport(false)
      setExportPwd('')
      mostrarToast(`${domicilis.length} domicilis exportats`)
    } catch (e) {
      setError(e.message)
    }
  }

  async function onFitxer(e) {
    const fitxer = e.target.files?.[0]
    if (!fitxer) return
    e.target.value = ''
    setError(null)
    try {
      if (await isEncrypted(fitxer)) {
        setPendingFile(fitxer)
        setShowImportPwd(true)
      } else {
        await aplicarImport(await importSlots(fitxer, null))
      }
    } catch (e) {
      setError(e.message)
    }
  }

  async function confirmarImportPwd() {
    try {
      await aplicarImport(await importSlots(pendingFile, importPwd.trim()))
      setShowImportPwd(false)
      setImportPwd('')
      setPendingFile(null)
    } catch (e) {
      setError(e.message)
    }
  }

  async function aplicarImport(items) {
    const existents = new Set(domicilis.map(d => d.codi))
    let nous = 0
    for (const item of items) {
      if (!existents.has(item.codi)) {
        const { id: _, ...rest } = item
        await domicilisDB.create(rest)
        nous++
      }
    }
    mostrarToast(`${nous} domicilis nous importats (${items.length - nous} duplicats ignorats)`)
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>Domicilis</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btnSec} onClick={() => importRef.current?.click()}>Importar</button>
          <button
            style={{ ...S.btnSec, opacity: domicilis.length ? 1 : 0.4 }}
            onClick={() => domicilis.length && setShowExport(true)}
          >
            Exportar
          </button>
          <button style={S.btnAdd} onClick={obrirNou}>+</button>
        </div>
      </header>

      <div style={S.llista}>
        {domicilis.length === 0 && (
          <div style={S.buit}>
            <p style={S.buitTitol}>Cap domicili registrat</p>
            <p style={S.buitHint}>Prem + per afegir el primer domicili HADO</p>
          </div>
        )}
        {domicilis.map(dom => {
          const estat = dom.dades_json?.estat
          return (
            <div key={dom.id} style={S.card} onClick={() => setDetallDom(dom)}>
              <div style={S.cardEsq}>
                <span style={S.codi}>{dom.codi}</span>
                {dom.dades_json?.lat && <span style={S.coordBadge}>📍</span>}
              </div>
              <div style={S.cardCentral}>
                <p style={S.adreca}>{dom.adreça || '—'}</p>
                {dom.dades_json?.persona_nom && (
                  <p style={S.notes}>👤 {dom.dades_json.persona_nom}</p>
                )}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                  {dom.dades_json?.dificultat && dom.dades_json.dificultat !== 'normal' && (
                    <BadgeDificultat dificultat={dom.dades_json.dificultat} />
                  )}
                  {(dom.dades_json?.num_visites ?? 1) > 1 && (
                    <span style={{ fontSize: 11, color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 7px', borderRadius: 12 }}>
                      🔄 {dom.dades_json.num_visites} visites
                    </span>
                  )}
                  {dom.dades_json?.notes && (
                    <p style={{ ...S.notes, margin: 0 }}>{dom.dades_json.notes}</p>
                  )}
                </div>
              </div>
              {estat && (
                <div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }}>
                  <span style={estat === 'ingres' ? S.badgeIngres : S.badgeAlta}>
                    {estat === 'ingres' ? '🟢 INGRÉS' : '🔴 ALTA'}
                  </span>
                </div>
              )}
              <div
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto', paddingLeft: 8 }}
              >
                <button
                  onClick={() => obrirEdicio(dom)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280', flexShrink: 0 }}
                >✎</button>
                <button
                  onClick={() => eliminar(dom)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'none', border: '1px solid #fee2e2', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#ef4444', flexShrink: 0 }}
                >✕</button>
              </div>
            </div>
          )
        })}
      </div>

      <input ref={importRef} type="file" accept=".hado" style={{ display: 'none' }} onChange={onFitxer} />

      {/* Fitxa de detall */}
      {detallDom && (
        <DetallModal
          dom={detallDom}
          onClose={() => setDetallDom(null)}
          onEdit={() => { setDetallDom(null); obrirEdicio(detallDom) }}
          onVeureTel={tel => setTelModal(tel)}
        />
      )}

      {/* Modal telèfon gran */}
      {telModal && (
        <TelModal telefon={telModal} onClose={() => setTelModal(null)} />
      )}

      {/* Modal: Formulari domicili */}
      {showForm && (
        <Overlay onClose={() => { setShowForm(false); setError(null) }}>
          <h2 style={S.modalTitol}>{editId ? 'Editar domicili' : 'Nou domicili'}</h2>

          <label style={S.label}>Codi HADO</label>
          <select style={S.select} value={form.codi} onChange={e => setF('codi', e.target.value)}>
            {codis.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <label style={S.label}>Adreça</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder="ex: Carrer de Balmes 1"
              value={form.adreça}
              onChange={e => setF('adreça', e.target.value)}
            />
            <button
              style={S.btnGeo}
              onClick={geocodificar}
              disabled={geocodant}
              title="Obtenir coordenades de l'adreça"
            >
              {geocodant ? '…' : '📍'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Latitud</label>
              <input
                style={S.input} type="number" step="0.000001"
                placeholder="41.3851"
                value={form.lat}
                onChange={e => setF('lat', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Longitud</label>
              <input
                style={S.input} type="number" step="0.000001"
                placeholder="2.1734"
                value={form.lng}
                onChange={e => setF('lng', e.target.value)}
              />
            </div>
          </div>

          {/* Secció informació addicional */}
          <div style={S.separador}>
            <div style={S.separadorLinia} />
            <span style={S.separadorText}>Informació addicional</span>
            <div style={S.separadorLinia} />
          </div>

          <label style={S.label}>Nom i cognoms</label>
          <input
            style={S.input}
            placeholder="ex: Joan Garcia Puig"
            value={form.persona_nom}
            onChange={e => setF('persona_nom', e.target.value)}
          />

          <label style={S.label}>Telèfon</label>
          <input
            style={S.input}
            type="tel"
            placeholder="ex: 934 123 456"
            value={form.telefon}
            onChange={e => setF('telefon', e.target.value)}
          />

          <label style={S.label}>Estat especial</label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {ESTATS.map(opt => (
              <button
                key={String(opt.val)}
                style={{
                  ...S.estatBtn,
                  backgroundColor: form.estat === opt.val ? opt.color ?? '#374151' : '#f3f4f6',
                  color:           form.estat === opt.val ? '#fff' : '#374151',
                }}
                onClick={() => setF('estat', opt.val)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <label style={S.label}>Dificultat</label>
          <select
            style={S.select}
            value={form.dificultat ?? 'normal'}
            onChange={e => setF('dificultat', e.target.value)}
          >
            {Object.entries(DIFICULTAT_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.emoji} {cfg.label}</option>
            ))}
          </select>

          <label style={S.label}>Múltiples visites</label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {[1, 2, 3].map(n => (
              <button
                key={n}
                style={{
                  flex: 1, border: '1px solid', borderRadius: 8,
                  padding: '8px 4px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  borderColor:     (form.num_visites ?? 1) === n ? '#007AFF' : '#e5e7eb',
                  backgroundColor: (form.num_visites ?? 1) === n ? '#007AFF' : '#f3f4f6',
                  color:           (form.num_visites ?? 1) === n ? '#fff'    : '#374151',
                }}
                onClick={() => setF('num_visites', n)}
              >
                {n === 1 ? 'Una' : n === 2 ? 'Dues' : 'Tres'} visita{n !== 1 ? 'es' : ''}
              </button>
            ))}
          </div>

          {(form.num_visites ?? 1) > 1 && (
            <>
              <label style={S.label}>Interval entre visites</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  style={{ ...S.input, width: 80, flex: 'none' }}
                  type="number" min={15} max={480} step={15}
                  value={form.interval_visites ?? 60}
                  onChange={e => setF('interval_visites', parseInt(e.target.value) || 60)}
                />
                <span style={{ fontSize: 14, color: '#6b7280' }}>minuts</span>
              </div>
            </>
          )}

          <label style={{ ...S.label, marginTop: 14 }}>Notes</label>
          <textarea
            style={{ ...S.input, height: 68, resize: 'vertical' }}
            placeholder="Indicacions, accés, referència visual…"
            value={form.notes}
            onChange={e => setF('notes', e.target.value)}
          />

          {error && <p style={S.error}>{error}</p>}
          <button style={S.btnPrimariBlock} onClick={guardar}>
            {editId ? 'Guardar canvis' : 'Crear domicili'}
          </button>
          {editId && (
            <button
              style={{ marginTop: 8, padding: '12px', borderRadius: 10, background: '#fff', color: '#FF9500', border: '1px solid #FF9500', cursor: 'pointer', width: '100%', fontSize: 15, fontWeight: 500, boxSizing: 'border-box' }}
              onClick={() => setMostrarConfirmacioAlta(true)}
            >
              🔴 Donar d'alta (buidar slot)
            </button>
          )}
        </Overlay>
      )}

      {/* Modal: Confirmació d'alta */}
      {mostrarConfirmacioAlta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%' }}>
            {!confirmat1 ? (
              <>
                <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>🔴 Donar d'alta {form.codi}</h3>
                <p style={{ fontSize: 14, color: '#444', lineHeight: 1.6, margin: '0 0 8px' }}>
                  Aquesta acció buidarà totes les dades del slot <strong>{form.codi}</strong>:
                </p>
                <ul style={{ fontSize: 13, color: '#666', margin: '0 0 8px', paddingLeft: 20, lineHeight: 2 }}>
                  <li>Nom i cognoms</li>
                  <li>Telèfon</li>
                  <li>Adreça</li>
                  <li>Notes i anotacions</li>
                  <li>Coordenades GPS</li>
                </ul>
                <p style={{ fontSize: 13, color: '#FF3B30', fontWeight: 500, margin: '0 0 16px' }}>
                  El slot {form.codi} quedarà disponible per a un nou pacient.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setMostrarConfirmacioAlta(false)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 14 }}>
                    Cancel·lar
                  </button>
                  <button onClick={() => setConfirmat1(true)} style={{ flex: 1, padding: 12, borderRadius: 10, background: '#FF9500', color: 'white', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                    Continuar →
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ color: '#FF3B30', margin: '0 0 12px', fontSize: 17, fontWeight: 700 }}>⚠️ Confirmació final</h3>
                <p style={{ fontSize: 14, color: '#444', lineHeight: 1.6, margin: '0 0 20px' }}>
                  Estàs segur que vols donar d'alta i buidar completament <strong>{form.codi}</strong>?
                  <br /><br />
                  <strong style={{ color: '#FF3B30' }}>Aquesta acció no es pot desfer.</strong>
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setConfirmat1(false); setMostrarConfirmacioAlta(false) }} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 14 }}>
                    Cancel·lar
                  </button>
                  <button onClick={handleAltaDomicili} style={{ flex: 1, padding: 12, borderRadius: 10, background: '#FF3B30', color: 'white', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                    🔴 Confirmar alta
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: Exportar */}
      {showExport && (
        <Overlay onClose={() => { setShowExport(false); setExportPwd(''); setError(null) }}>
          <h2 style={S.modalTitol}>Exportar {domicilis.length} domicilis</h2>
          <label style={S.label}>Contrasenya (opcional)</label>
          <input
            style={S.input} type="password"
            placeholder="Buit = sense encriptació"
            value={exportPwd}
            onChange={e => setExportPwd(e.target.value)}
            autoFocus
          />
          <p style={S.hint}>Amb contrasenya el fitxer queda encriptat amb AES-256.</p>
          {error && <p style={S.error}>{error}</p>}
          <button style={S.btnPrimariBlock} onClick={confirmarExport}>Descarregar .hado</button>
        </Overlay>
      )}

      {/* Modal: Contrasenya importació */}
      {showImportPwd && (
        <Overlay onClose={() => { setShowImportPwd(false); setImportPwd(''); setPendingFile(null) }}>
          <h2 style={S.modalTitol}>Fitxer encriptat</h2>
          <p style={S.hint}>Introdueix la contrasenya per desencriptar el fitxer .hado.</p>
          <label style={S.label}>Contrasenya</label>
          <input
            style={S.input} type="password"
            value={importPwd}
            onChange={e => setImportPwd(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && confirmarImportPwd()}
            autoFocus
          />
          {error && <p style={S.error}>{error}</p>}
          <button style={S.btnPrimariBlock} onClick={confirmarImportPwd}>Desencriptar i importar</button>
        </Overlay>
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  )
}

const S = {
  page:      { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f9fafb' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  title:     { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
  llista:    { flex: 1, overflowY: 'auto', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  buit:      { textAlign: 'center', marginTop: 60 },
  buitTitol: { fontSize: 16, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' },
  buitHint:  { fontSize: 14, color: '#9ca3af', margin: 0 },

  // ── Card de la llista ───────────────────────────────────────────────────────
  card:        { backgroundColor: '#fff', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, cursor: 'pointer', overflow: 'hidden', width: '100%', boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' },
  cardEsq:     { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 56 },
  codi:        { fontSize: 13, fontWeight: 700, color: '#007AFF' },
  coordBadge:  { fontSize: 11 },
  cardCentral: { flex: 1, minWidth: 0 },
  adreca:      { margin: 0, fontSize: 13, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  notes:       { margin: '2px 0 0', fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardAccions: { display: 'flex', flexDirection: 'row', gap: 4, flexShrink: 0, alignItems: 'center' },
  btnEdit:     { background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 14, color: '#6b7280' },
  btnDel:      { background: 'none', border: '1px solid #fee2e2', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: '#ef4444' },

  // ── Badges d'estat ──────────────────────────────────────────────────────────
  badgeIngres: { fontSize: 10, fontWeight: 700, color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' },
  badgeAlta:   { fontSize: 10, fontWeight: 700, color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' },

  // ── Botons capçalera ────────────────────────────────────────────────────────
  btnAdd: { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, width: 36, height: 36, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  btnSec: { backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' },

  // ── Overlay / Modal base ────────────────────────────────────────────────────
  overlayFons:      { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' },
  overlayContingut: { backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', width: '100%', maxHeight: '92dvh', overflowY: 'auto', position: 'relative' },
  closeBtn:         { position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', padding: 4 },
  modalTitol:       { margin: '0 0 14px', fontSize: 18, fontWeight: 700, color: '#111827' },

  // ── Fitxa de detall ─────────────────────────────────────────────────────────
  detallAdreca:   { fontSize: 14, color: '#6b7280', margin: '0 0 12px' },
  estatBadgeGran: { fontSize: 14, fontWeight: 700, padding: '6px 14px', borderRadius: 8, border: '1px solid', marginBottom: 14, display: 'inline-block' },
  detallFila:     { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  detallIcon:     { fontSize: 16, flexShrink: 0, marginTop: 1 },
  detallText:     { fontSize: 15, color: '#111827', flex: 1 },
  btnEditModal:   { background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', fontSize: 13, color: '#6b7280', cursor: 'pointer', flexShrink: 0 },
  btnTrucar:      { flex: 1, padding: '8px 12px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },

  // ── Modal telèfon gran ──────────────────────────────────────────────────────
  telNumero: { fontSize: 28, fontWeight: 700, color: '#111827', textAlign: 'center', margin: '16px 0 20px', letterSpacing: '0.06em' },

  // ── Formulari ───────────────────────────────────────────────────────────────
  label:  { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, marginTop: 12 },
  input:  { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa' },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa' },
  btnGeo: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0 12px', fontSize: 16, cursor: 'pointer' },

  separador:     { display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 2px' },
  separadorLinia:{ flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  separadorText: { fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' },

  estatBtn: { flex: 1, border: '1px solid transparent', borderRadius: 8, padding: '7px 4px', fontSize: 12, fontWeight: 500, cursor: 'pointer' },

  hint:            { margin: '6px 0 0', fontSize: 12, color: '#9ca3af' },
  error:           { margin: '8px 0 0', fontSize: 13, color: '#ef4444' },
  btnPrimariBlock: { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', width: '100%', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
  toast: { position: 'fixed', bottom: 72, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111827', color: '#fff', padding: '8px 18px', borderRadius: 8, fontSize: 14, zIndex: 9999, whiteSpace: 'nowrap' },
}
